import React, { useState, useEffect } from 'react';
import DataTable from '../../../components/ui/DataTable';
import { budgetQuotationService } from '../services/budgetQuotationService';
import { orderingQuotationService } from '../../ordering/services/orderingQuotationService';
import { Eye, Download, User, Plus, FileText, Edit3, Wallet } from 'lucide-react';
import { downloadPdfFromUrl } from '../../ordering/utils/downloadPdf';
import { API_BASE_URL } from '../../../services/api';
import toast from 'react-hot-toast';
import { Modal, Pagination, FilterSection, ActionView, ActionEdit, ActionDownload, ActionAdd, ActionDocument, ActionDelete } from '../../../components/ui';
import { PdfViewer } from './pdfviewer';
import { useClientPagination } from '@/hooks';
import { useVerveContacts } from '../../verve-contacts/hooks/useVerveContacts';
import { InitialQuotationModal } from './InitialQuotationModal';
import { CustomerFormModal } from '../../verve-contacts/components/CustomerFormModal';
import ErrorBoundary from '@/components/ui/ErrorBoundary';
import { useAuthStore } from '@/store/authStore';
import { useNavigate } from 'react-router-dom';
import { CreditPaymentForm } from '../../verve-accounts/components/CreditPaymentForm';
import API from '@/lib/API';

export const BudgetTableView = ({ onProceedToPrep, onViewClient, onStartOrdering, highlightedRowId }) => {
  const { contacts: contactsData } = useVerveContacts(); // Fetch all to allow local filtering of initial_quotation and budget
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    status: "all",
    date: "all",
    fromDate: "",
    toDate: ""
  });
  const { user } = useAuthStore();
  const isOrderingOnlyRole = ['client_coordinator'].includes(user?.role);
  const isQuotationOnlyRole = ['field_marketing', 'designer'].includes(user?.role);
  const [stageFilter, setStageFilter] = useState(() => {
    if (isOrderingOnlyRole) return 'ordering';
    if (isQuotationOnlyRole) return 'quotation';
    return 'all';
  }); // 'all' | 'quotation' | 'ordering'

  useEffect(() => {
    if (['client_coordinator'].includes(user?.role)) {
      setStageFilter('ordering');
    } else if (['field_marketing', 'designer'].includes(user?.role)) {
      setStageFilter('quotation');
    }
  }, [user?.role]);

  // PDF View state
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedPdfDoc, setSelectedPdfDoc] = useState(null);

  // Delete state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);

  // Initial Quotation state
  const [isInitialQuotationOpen, setIsInitialQuotationOpen] = useState(false);
  const [selectedContactForInitialQuote, setSelectedContactForInitialQuote] = useState(null);
  const [selectedQuotationForEdit, setSelectedQuotationForEdit] = useState(null);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  // Received Amount Modal state
  const [isCreditPaymentFormOpen, setIsCreditPaymentFormOpen] = useState(false);
  const [selectedProjectForPayment, setSelectedProjectForPayment] = useState(null);


  const fetchQuotations = async () => {
    setLoading(true);
    try {
      const [budgetRes, orderingRes] = await Promise.all([
        budgetQuotationService.getQuotations().catch(() => ({ success: false, data: [] })),
        orderingQuotationService.getQuotations().catch(() => ({ success: false, data: [] }))
      ]);

      const bData = (budgetRes?.success && budgetRes.data) ? budgetRes.data : [];
      const oData = (orderingRes?.success && orderingRes.data)
        ? orderingRes.data.map(q => ({
            ...q,
            quotationType: 'Ordering',
            status: q.status || (q.paymentData ? 'Payment Completed' : 'Draft')
          }))
        : [];

      const seen = new Set();
      const merged = [...bData, ...oData].filter(item => {
        if (!item._id) return true;
        const idStr = String(item._id);
        if (seen.has(idStr)) return false;
        seen.add(idStr);
        return true;
      });
      setData(merged);
    } catch (error) {
      console.error("Failed to fetch quotations:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle quote status change from dropdown
  const handleQuoteStatusChange = async (row, newStatus) => {
    const id = row._id;
    try {
      // Optimistically update local state
      setData(prev => prev.map(q => q._id === id ? { ...q, status: newStatus } : q));
      await budgetQuotationService.updateStatus(id, newStatus);
    } catch (err) {
      console.error('Failed to update quote status:', err);
      toast.error('Failed to update status');
      // Revert on error
      setData(prev => prev.map(q => q._id === id ? { ...q, status: row.status } : q));
    }
  };

  useEffect(() => {
    fetchQuotations();
  }, []);

  const handleViewPdf = (row) => {
    if (row.pdfUrl) {
      setSelectedPdfDoc({
        fileUrl: row.pdfUrl.startsWith('http') ? row.pdfUrl : `${API_BASE_URL}${row.pdfUrl}`,
        title: `BudgetQuotation_${row.quotationNo || 'VK'}.pdf`
      });
      setIsViewModalOpen(true);
    }
  };

  const handleDeleteQuotation = (row) => {
    setRowToDelete(row);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!rowToDelete) return;
    try {
      await budgetQuotationService.deleteQuotation(rowToDelete._id);
      toast.success("Quotation deleted successfully!");
      fetchQuotations();
    } catch (err) {
      toast.error("Failed to delete quotation");
    } finally {
      setIsDeleteModalOpen(false);
      setRowToDelete(null);
    }
  };

  const getContactForBudget = (row) => {
    if (!contactsData) return null;
    let match = null;
    if (row.contactRef) {
      match = contactsData.find(c => c._id === row.contactRef || c.customId === row.contactRef);
    }
    if (!match && row.projectName) {
      match = contactsData.find(c => {
        const name = (c.customerName || `${c.firstName || ''} ${c.lastName || ''}`).trim().toLowerCase();
        return name === (row.clientName || row.projectName || '').trim().toLowerCase();
      });
    }
    return match;
  };

  const getStageInfo = (row) => {
    const isDirect = row.salesType === 'Direct Sale' ||
                     row.isDirectSale === true ||
                     row.contactObj?.salesType === 'Direct Sale' ||
                     row.contactObj?.isDirectSale === true;
    if (isDirect) {
      return { key: 'ordering', label: 'Orderings', badgeClass: 'text-orange-700 border-orange-300 bg-orange-50', dotClass: 'bg-orange-500' };
    }
    const contact = row.contactObj || row;
    const contactStatus = String(contact?.leadStatus || contact?.status || contact?.teleStatus || contact?.currentStage || contact?.projectStatus || '').toLowerCase().trim();

    let stageKey = row.rowStageKey;
    if ((!row.quotationNo || row.quotationNo === '-' || row.isContactOnly || !stageKey) && contactStatus) {
      if (['order_placing', 'ordering', 'orderings', 'final_payment', 'delivery', 'production'].includes(contactStatus)) {
        stageKey = 'ordering';
      } else if (['budget', 'sign_of_copy', 'prod_drawings', 'final_quotation', 'final quotation'].includes(contactStatus)) {
        stageKey = 'budget';
      } else if (['initial_design', 'initial_quotation', 'initial quotation', 'initial', 'quote', 'quotation', 'lead', 'design_proposal', 'design proposal'].includes(contactStatus)) {
        stageKey = 'initial_quotation';
      }
    }
    if (!stageKey && contactStatus) {
      if (['order_placing', 'ordering', 'orderings', 'final_payment', 'delivery', 'production'].includes(contactStatus)) {
        stageKey = 'ordering';
      } else if (['budget', 'sign_of_copy', 'prod_drawings', 'final_quotation', 'final quotation'].includes(contactStatus)) {
        stageKey = 'budget';
      } else if (['initial_design', 'initial_quotation', 'initial quotation', 'initial', 'quote', 'quotation', 'lead', 'design_proposal', 'design proposal'].includes(contactStatus)) {
        stageKey = 'initial_quotation';
      }
    }
    if (!stageKey) {
      stageKey = (row.quotationType === 'Ordering' ? 'ordering' : row.quotationType === 'Final' ? 'budget' : 'initial_quotation');
    }

    if (stageKey === 'ordering') {
      return { key: 'ordering', label: 'Orderings', badgeClass: 'text-orange-700 border-orange-300 bg-orange-50', dotClass: 'bg-orange-500' };
    }
    if (stageKey === 'budget') {
      return { key: 'budget', label: 'Budget', badgeClass: 'text-amber-700 border-amber-300 bg-amber-50', dotClass: 'bg-amber-500' };
    }
    return { key: 'initial_quotation', label: 'Initial Quotation', badgeClass: 'text-cyan-600 border-cyan-200 bg-white', dotClass: 'bg-cyan-500' };
  };

  const columns = [
    {
      header: 'Verve ID',
      accessor: (row) => row.contactObj?.customId || '-',
      cellClassName: '!font-normal text-brand-blue !whitespace-nowrap'
    },
    {
      header: 'Customer Info',
      cellClassName: '!font-normal !whitespace-nowrap',
      accessor: (row) => {
        const c = row.contactObj;
        if (!c) return <span className="font-normal text-slate-800">{row.projectName || row.clientName}</span>;
        const name = c.customerName || `${c.firstName || ''} ${c.lastName || ''}`.trim() || row.projectName;
        const phone = c.mobile || c.phoneNumber || c.phone || '';
        const email = c.email || '';
        return (
          <div className="flex flex-col min-w-0">
            <span className="font-normal text-slate-800 flex items-center gap-2">{name}</span>
            <span className="font-normal text-[11px] text-slate-500 flex items-center gap-1">
              {phone || "No Phone"}
              {email && <span className="mx-1 opacity-50">|</span>}
              {email && email.toLowerCase()}
            </span>
          </div>
        );
      }
    },
    {
      header: 'Address', accessor: (row) => {
        const c = row.contactObj;
        if (!c) return row.site || '-';
        const addressStr = `${c.city || ''}, ${c.state || ''}`.replace(/^,\s*|,\s*$/g, '') || row.site || '-';
        return <div className="truncate max-w-[150px] font-normal" title={addressStr}>{addressStr}</div>;
      },
      cellClassName: '!text-xs !font-normal'
    },
    {
      header: 'Status',
      accessor: (row) => {
        const stageInfo = getStageInfo(row);
        return (
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[9px] font-bold tracking-wider uppercase border ${stageInfo.badgeClass}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${stageInfo.dotClass}`}></span>
              {stageInfo.label}
            </span>
          </div>
        );
      },
      cellClassName: '!font-normal !whitespace-nowrap'
    },
    { header: 'Source', accessor: (row) => row.contactObj?.source || '-', cellClassName: '!uppercase !text-[10px] !tracking-wider text-slate-500 !whitespace-nowrap !font-normal' },
    { header: 'Reference Type', accessor: (row) => row.contactObj?.leadType || row.contactObj?.referenceType || '-', cellClassName: '!text-xs text-slate-600 !whitespace-nowrap !font-normal' },
    {
      header: 'Sale Type',
      accessor: (row) => {
        const isDirect = row.isDirectSale || row.salesType === 'Direct Sale' || row.contactObj?.salesType === 'Direct Sale' || row.contactObj?.isDirectSale === true;
        return (
          <span className={`px-2 py-0.5 rounded text-[10px] font-semibold tracking-wide border ${
            isDirect 
              ? 'bg-purple-50 text-purple-700 border-purple-200' 
              : 'bg-blue-50 text-blue-700 border-blue-200'
          }`}>
            {isDirect ? 'Direct Sale' : 'Project'}
          </span>
        );
      },
      cellClassName: '!whitespace-nowrap'
    },
    { 
      header: 'Staff Name', 
      accessor: (row) => {
        const assignee = row.contactObj?.assignedTo || row.contactObj?.assignee || row.contactObj?.telecaller;
        if (!assignee) return 'Unassigned';
        if (typeof assignee === 'string') return assignee;
        return (assignee.name || `${assignee.firstName || ''} ${assignee.lastName || ''}`.trim()) || 'Unassigned';
      }, 
      cellClassName: '!text-xs text-slate-600 !whitespace-nowrap !font-normal' 
    },
    // { header: 'Project Name', accessor: (row) => row.projectName || '-', cellClassName: '!font-normal !whitespace-nowrap' },
    { header: 'Quotation No', accessor: (row) => row.quotationNo || '-', cellClassName: '!font-normal !whitespace-nowrap' },
    { header: 'Site', accessor: (row) => row.site || '-', cellClassName: '!font-normal !whitespace-nowrap' },
    { header: 'Prepared by', accessor: (row) => row.referenceDesigner || '-', cellClassName: '!font-normal !whitespace-nowrap' },
    { header: 'Created Date', accessor: (row) => row.date || '-', cellClassName: '!font-normal !whitespace-nowrap' },
    {
      header: 'Order Status',
      accessor: (row) => {
        const hasQuoteNo = row.quotationNo && row.quotationNo !== '-';
        const isOrderingQuote = row.quotationType === 'Ordering';
        const hasExplicitConfirm = row.isConfirmOrder !== undefined || row.isConformOrder !== undefined;

        if (!hasQuoteNo || (!isOrderingQuote && !hasExplicitConfirm)) {
          return (
            <span className="px-2 py-0.5 border rounded text-[11px] font-semibold bg-slate-50 text-slate-600 border-slate-200 uppercase">
              Not Started
            </span>
          );
        }

        const isChecked = row.isConfirmOrder !== false && row.isConformOrder !== false && row.isConfirmOrder !== 'false' && row.isConformOrder !== 'false';
        return (
          <span className={`px-2 py-0.5 border rounded text-[11px] font-semibold ${
            isChecked 
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
              : 'bg-red-50 text-red-700 border-red-200'
          }`}>
            {isChecked ? 'Conformed' : 'Not conformed'}
          </span>
        );
      },
      cellClassName: '!whitespace-nowrap !py-1 text-center'
    },
    {
      header: 'Grand Total',
      accessor: (row) => row.totals?.grandTotal
        ? (row.totals.grandTotal.toString().startsWith('₹') ? row.totals.grandTotal : `₹${Number(row.totals.grandTotal).toLocaleString('en-IN')}`)
        : '-',
      cellClassName: '!font-normal !whitespace-nowrap'
    },
    {
      header: 'Quote Status',
      minWidth: '130px',
      render: (row) => {
        // Normalize legacy/old DB values to the 3 display values
        const normalizeQuoteStatus = (s) => {
          if (!s || ['Draft', 'Pending', 'Not Started'].includes(s)) return 'Not Started';
          if (['In Progress'].includes(s)) return 'In Progress';
          if (['Completed', 'Approved', 'Payment Completed', 'Paid'].includes(s)) return 'Completed';
          return 'Not Started';
        };
        const currentStatus = normalizeQuoteStatus(row.status);
        const isCompleted = currentStatus === 'Completed';
        return (
          <div className="text-center" onClick={(e) => e.stopPropagation()}>
            <select
              value={currentStatus}
              onChange={(e) => handleQuoteStatusChange(row, e.target.value)}
              disabled={isCompleted}
              className={`text-[10px] uppercase text-brand-dark bg-white border border-slate-200 rounded px-2 py-1 transition-colors focus:outline-none focus:ring-1 focus:ring-brand-blue ${
                isCompleted ? 'cursor-not-allowed opacity-70 bg-slate-50' : 'cursor-pointer hover:border-brand-blue'
              }`}
            >
              <option value="Not Started">Not Started</option>
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
            </select>
          </div>
        );
      }
    },
    {
      header: 'Status',
      minWidth: '100px',
      render: (row) => {
        const normalizeQuoteStatus = (s) => {
          if (!s || ['Draft', 'Pending', 'Not Started'].includes(s)) return 'Not Started';
          if (['In Progress'].includes(s)) return 'In Progress';
          if (['Completed', 'Approved', 'Payment Completed', 'Paid'].includes(s)) return 'Completed';
          return 'Not Started';
        };
        const currentStatus = normalizeQuoteStatus(row.status);
        const colorMap = {
          'Not Started': 'bg-slate-100 text-slate-500 border-slate-200',
          'In Progress': 'bg-amber-50 text-amber-600 border-amber-200',
          'Completed': 'bg-emerald-50 text-emerald-600 border-emerald-200',
        };
        const dotMap = {
          'Not Started': 'bg-slate-400',
          'In Progress': 'bg-amber-400',
          'Completed': 'bg-emerald-500',
        };
        const colorClass = colorMap[currentStatus];
        const dotClass = dotMap[currentStatus];
        return (
          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-normal uppercase tracking-wider whitespace-nowrap ${colorClass}`}>
            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dotClass}`} />
            {currentStatus}
          </span>
        );
      }
    },
    {
      header: 'Actions',
      accessor: (row) => {
        const stageInfo = getStageInfo(row);
        const isStub = !row.quotationNo || row.quotationNo === '-';
        const effectivelyContactOnly = row.isContactOnly || isStub;
        const isDirect = row.isDirectSale || row.salesType === 'Direct Sale' || row.contactObj?.salesType === 'Direct Sale' || row.contactObj?.isDirectSale === true;

        return (
          <ErrorBoundary>
            <div className="flex items-center gap-2">
              <ActionView 
                onClick={() => onViewClient && onViewClient(row.contactObj || { 
                  _id: row.contactRef, 
                  firstName: row.projectName || row.clientName || 'Unknown', 
                  customId: 'Unlinked Budget' 
                })} 
                title="View Client Details"
              />

              {stageInfo.key === 'initial_quotation' || stageInfo.key === 'new' ? (
                // Stage: Initial Quotation
                effectivelyContactOnly ? (
                  <ActionAdd 
                    onClick={() => onProceedToPrep && onProceedToPrep(row.contactObj || row, null, true)}
                    title="Start Initial Quotation"
                  />
                ) : (
                  <>
                    <ActionEdit 
                      onClick={() => onProceedToPrep && onProceedToPrep(row.contactObj || row, row, true)}
                      title="Update Initial Quotation"
                    />
                    <ActionDownload 
                      onClick={() => {
                        if (row.pdfUrl) downloadPdfFromUrl(`${API_BASE_URL}${row.pdfUrl}`, `InitialQuotation_${row.quotationNo || 'VK'}.pdf`);
                        else toast.error("Initial Quotation PDF not yet generated.");
                      }}
                      title="Download Initial Quotation"
                    />
                  </>
                )
              ) : stageInfo.key === 'ordering' ? (
                // Stage: Ordering
                <ActionAdd 
                  onClick={() => onStartOrdering && onStartOrdering(row)}
                  title="Create Order"
                />
              ) : (
                // Stage: Budget
                effectivelyContactOnly ? (
                  <ActionAdd 
                    onClick={() => onProceedToPrep && onProceedToPrep(row.contactObj || row, row)}
                    title="Start Final Quotation"
                  />
                ) : (
                  <>
                    <ActionEdit 
                      onClick={() => onProceedToPrep && onProceedToPrep(row.contactObj || row, row)}
                      title="Update Final Quotation"
                    />
                    <ActionDownload 
                      onClick={() => {
                        if (row.pdfUrl) downloadPdfFromUrl(`${API_BASE_URL}${row.pdfUrl}`, `FinalQuotation_${row.quotationNo || 'VK'}.pdf`);
                        else toast.error("Final Quotation PDF not yet generated.");
                      }}
                      title="Download Final Quotation"
                    />
                  </>
                )
              )}
              {(user?.role === 'owner' || user?.role === 'admin') && !effectivelyContactOnly && row.status !== 'Payment Completed' && (
                <ActionDelete 
                  onClick={() => handleDeleteQuotation(row)}
                  title="Delete Quotation"
                />
              )}
              {isDirect && (() => {
                const hasPreparedOrder = row.quotationType === 'Ordering' && row.quotationNo && row.quotationNo !== '-';
                return (
                  <button
                    className={`p-1 rounded transition-colors ${
                      hasPreparedOrder
                        ? 'text-emerald-600 hover:bg-emerald-50 cursor-pointer'
                        : 'text-slate-400 hover:bg-slate-100 cursor-pointer opacity-60'
                    }`}
                    title="Add Received Amount"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!hasPreparedOrder) {
                        toast.error("No Orderings detected");
                        return;
                      }
                      const pId = row.contactObj?._id || row.contactRef || row._id;
                      setSelectedProjectForPayment({
                        _id: pId,
                        projectId: pId,
                        customerName: row.contactObj?.customerName || row.contactObj?.firstName || row.projectName || row.clientName,
                        customerCustomId: row.contactObj?.customId || row.customId || '-',
                        customId: row.contactObj?.customId || row.customId || '-',
                        model: 'VerveContact'
                      });
                      setIsCreditPaymentFormOpen(true);
                    }}
                  >
                    <Wallet size={16} />
                  </button>
                );
              })()}
            </div>
          </ErrorBoundary>
        );
      }
    }
  ];

  const combinedData = React.useMemo(() => {
    const result = [];
    const getLevel = (contact) => {
      const status = String(contact?.leadStatus || contact?.status || contact?.teleStatus || '').toLowerCase().trim();
      if (['order_placing', 'ordering', 'orderings', 'final_payment', 'delivery'].includes(status)) return 3;
      if (['budget', 'sign_of_copy', 'prod_drawings'].includes(status)) return 2;
      return 1;
    };

    const getStagePriority = (stKey) => {
      if (stKey === 'ordering') return 3;
      if (stKey === 'budget') return 2;
      return 1;
    };

    const projectMap = new Map();

    data.forEach(quotation => {
      const contact = getContactForBudget(quotation) || quotation.contactObj;
      const verveId = contact?.customId || quotation.contactObj?.customId || quotation.verveId || quotation.customId;
      const contactId = contact?._id ? contact._id.toString() : (quotation.contactRef || '').toString();
      const nameKey = (contact?.customerName || quotation.clientName || quotation.projectName || '').trim().toLowerCase();

      // Deduplicate strictly by Verve ID first, then Contact ID, then exact Name, fallback to row _id:
      const groupKey = verveId || contactId || nameKey || quotation._id;
      if (!groupKey) return;

      const isDirect = quotation.salesType === 'Direct Sale' ||
                       quotation.isDirectSale === true ||
                       contact?.salesType === 'Direct Sale' ||
                       contact?.isDirectSale === true;
      const type = isDirect ? 'Ordering' : (quotation.quotationType || (quotation.type === 'initial_quotation' || quotation.quotationNo?.includes('VK/INT') ? 'Initial' : 'Final'));
      const isStub = !quotation.quotationNo || quotation.quotationNo === '-' || quotation.isContactOnly;
      const contactStatus = String(contact?.leadStatus || contact?.status || contact?.teleStatus || contact?.currentStage || contact?.projectStatus || '').toLowerCase().trim();

      if (isStub && contactStatus) {
        const earlyStages = ['new', 'contacted', 'appointment_scheduled', 'site_visit_requested', 'site_visit_completed', 'follow_up', 'not_interested'];
        if (earlyStages.includes(contactStatus)) {
          return;
        }
      }

      let stageKey = isDirect ? 'ordering' : null;
      if (!stageKey && isStub && contactStatus) {
        if (['order_placing', 'ordering', 'orderings', 'final_payment', 'delivery', 'production'].includes(contactStatus)) {
          stageKey = 'ordering';
        } else if (['budget', 'sign_of_copy', 'prod_drawings', 'final_quotation', 'final quotation'].includes(contactStatus)) {
          stageKey = 'budget';
        } else if (['initial_design', 'initial_quotation', 'initial quotation', 'initial', 'quote', 'quotation', 'lead', 'design_proposal', 'design proposal'].includes(contactStatus)) {
          stageKey = 'initial_quotation';
        }
      }
      
      // If it's a stub, not a direct sale, and didn't match any explicitly permitted stage, skip it.
      if (!stageKey && isStub && !isDirect) {
        return;
      }

      if (!stageKey) {
        stageKey = isDirect ? 'ordering' : (type === 'Ordering' ? 'ordering' : type === 'Final' ? 'budget' : 'initial_quotation');
      }

      // Ensure ONE single record per contact/project across all stages:
      if (!projectMap.has(groupKey)) {
        projectMap.set(groupKey, { ...quotation, contactObj: contact, rowStageKey: stageKey });
      } else {
        const existing = projectMap.get(groupKey);
        const existingPrio = getStagePriority(existing.rowStageKey);
        const newPrio = getStagePriority(stageKey);

        const existingIsStub = !existing.quotationNo || existing.quotationNo === '-';
        const isStub = !quotation.quotationNo || quotation.quotationNo === '-';

        // 1. If new quotation has higher stage priority (progressed Initial -> Budget -> Ordering), update!
        if (newPrio > existingPrio) {
          projectMap.set(groupKey, { ...quotation, contactObj: contact, rowStageKey: stageKey });
        }
        // 2. If same stage priority, prefer real quotation over stub, or newest createdAt
        else if (newPrio === existingPrio) {
          if (existingIsStub && !isStub) {
            projectMap.set(groupKey, { ...quotation, contactObj: contact, rowStageKey: stageKey });
          } else if (!existingIsStub && !isStub) {
            if (new Date(quotation.createdAt || 0) > new Date(existing.createdAt || 0)) {
              projectMap.set(groupKey, { ...quotation, contactObj: contact, rowStageKey: stageKey });
            }
          }
        }
      }
    });

    projectMap.forEach(row => {
      result.push({
        ...row,
        isContactOnly: !row.quotationNo || row.quotationNo === '-' || row.isContactOnly
      });
    });

    return result.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  }, [data, contactsData]);

  const filteredData = combinedData.filter(item => {
    // Search
    let matchesSearch = true;
    if (search) {
      const searchLower = search.toLowerCase();
      matchesSearch = (item.projectName || '').toLowerCase().includes(searchLower) ||
        (item.quotationNo || '').toLowerCase().includes(searchLower) ||
        (item.contactObj?.customerName || '').toLowerCase().includes(searchLower) ||
        (item.contactObj?.firstName || '').toLowerCase().includes(searchLower);
    }
    if (!matchesSearch) return false;

    // Stage Filter (Quotation = initial_quotation + budget, Ordering = ordering)
    if (isOrderingOnlyRole || stageFilter === 'ordering') {
      if (item.rowStageKey !== 'ordering') return false;
    } else if (stageFilter === 'quotation') {
      if (item.rowStageKey === 'ordering') return false;
    }

    // Status Filter
    if (filters.status !== "all" && item.status?.toLowerCase() !== filters.status.toLowerCase()) {
      return false;
    }

    // Date Filter (fromDate and toDate)
    const rawDate = item.date || item.createdAt || item.contactObj?.createdAt;
    if (filters.fromDate || filters.toDate) {
      const itemDate = new Date(rawDate);
      if (filters.fromDate) {
        const from = new Date(filters.fromDate);
        from.setHours(0, 0, 0, 0);
        if (itemDate < from) return false;
      }
      if (filters.toDate) {
        const to = new Date(filters.toDate);
        to.setHours(23, 59, 59, 999);
        if (itemDate > to) return false;
      }
    } else if (filters.date !== "all") {
      const itemDate = new Date(rawDate);
      const today = new Date();
      if (filters.date === "today") {
        if (itemDate.toDateString() !== today.toDateString()) return false;
      } else if (filters.date === "this_week") {
        const first = today.getDate() - today.getDay();
        const firstDay = new Date(today.setDate(first));
        if (itemDate < firstDay) return false;
      } else if (filters.date === "this_month") {
        if (itemDate.getMonth() !== today.getMonth() || itemDate.getFullYear() !== today.getFullYear()) return false;
      } else if (filters.date === "this_year") {
        if (itemDate.getFullYear() !== today.getFullYear()) return false;
      }
    }

    return true;
  });

  const { paginatedData, totalPages } = useClientPagination(filteredData, page, 10);

  const statusOptions = [
    { value: 'Not Started', label: 'Not Started' },
    { value: 'In Progress', label: 'In Progress' },
    { value: 'Completed', label: 'Completed' }
  ];

  return (
      <div className="flex-1 flex flex-col gap-3 w-full">
      <FilterSection
        search={search}
        setSearch={(val) => {
          setSearch(val);
          setPage(1);
        }}
        filters={filters}
        setFilters={(key, val) => {
          if (typeof key === 'object') {
            setFilters(prev => ({ ...prev, ...key }));
          } else {
            setFilters(prev => ({ ...prev, [key]: val }));
          }
          setPage(1);
        }}
        statusOptions={statusOptions}
        statusPlaceholder="All Statuses"
        hideViewToggle={true}
        hideFilterButton={true}
        onAddClick={isQuotationOnlyRole ? undefined : () => setIsContactModalOpen(true)}
        addLabel={isQuotationOnlyRole ? undefined : "Add Sale"}
      >
        {/* Stage Filter Dropdown */}
        <div className="shrink-0">
          <select
            value={isOrderingOnlyRole ? 'ordering' : isQuotationOnlyRole ? 'quotation' : stageFilter}
            disabled={isOrderingOnlyRole || isQuotationOnlyRole}
            onChange={e => { if (!isOrderingOnlyRole && !isQuotationOnlyRole) { setStageFilter(e.target.value); setPage(1); } }}
            className={`text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-blue/30 text-slate-700 min-w-[140px] ${
              (isOrderingOnlyRole || isQuotationOnlyRole) ? 'bg-slate-100 opacity-80 cursor-not-allowed' : 'bg-white cursor-pointer'
            }`}
          >
            {!isOrderingOnlyRole && !isQuotationOnlyRole && <option value="all">All Records</option>}
            {!isOrderingOnlyRole && <option value="quotation">Quotation</option>}
            {!isQuotationOnlyRole && <option value="ordering">Ordering</option>}
          </select>
        </div>
      </FilterSection>
      <div className="flex-1 flex flex-col bg-white rounded-xl shadow-sm border border-slate-200">
        <DataTable
          columns={columns}
          data={paginatedData}
          onRowClick={(row) => {
            if (user?.role === 'client_coordinator') {
               const id = row?.contactObj?._id || row?.contactRef || row?.leadId || row?._id;
               if (id) {
                 navigate(`/lead-executions/${id}`);
               }
            }
          }}
          isLoading={loading}
          loadingText="Loading budgets..."
          noScroll={true}
          getRowClassName={(row) => {
            const rowId = row?._id || row?.id;
            const contactRef = row?.contactRef || row?.contactObj?._id;
            if (highlightedRowId && (
              String(rowId) === String(highlightedRowId) ||
              String(contactRef) === String(highlightedRowId)
            )) {
              return "!bg-amber-100 !border-l-4 !border-l-amber-500";
            }
            return "";
          }}
        />
        {totalPages > 1 && (
          <div className="p-4 border-t border-slate-200 bg-white">
            <Pagination
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>

      {/* PDF Viewer Modal */}
      <Modal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        title={selectedPdfDoc?.title || "Quotation Document"}
        className="max-w-6xl w-full"
      >
        <div className="-m-5 sm:-m-6 h-[85vh] flex flex-col rounded-b-lg sm:rounded-b-xl overflow-hidden">
          <PdfViewer
            selectedDocument={selectedPdfDoc}
            onClose={() => setIsViewModalOpen(false)}
          />
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete Quotation"
        size="md"
      >
        <div className="p-6 text-left">
          <p className="text-[14px] text-slate-700 mb-6 font-medium leading-relaxed">
            Are you sure you want to delete this quotation? This will also permanently remove the associated PDF from Google Drive.
          </p>
          <div className="flex justify-end gap-3 mt-4">
            <button
              onClick={() => setIsDeleteModalOpen(false)}
              className="px-5 py-2 text-[13px] font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors border border-slate-200"
            >
              Cancel
            </button>
            <button
              onClick={confirmDelete}
              className="px-5 py-2 text-[13px] font-bold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors shadow-sm shadow-red-600/20"
            >
              Confirm Delete
            </button>
          </div>
        </div>
      </Modal>

      {/* Initial Quotation Modal */}
      <InitialQuotationModal
        isOpen={isInitialQuotationOpen}
        onClose={() => {
          setIsInitialQuotationOpen(false);
          setSelectedContactForInitialQuote(null);
          setSelectedQuotationForEdit(null);
        }}
        contact={selectedContactForInitialQuote}
        existingData={selectedQuotationForEdit}
        onSaved={fetchQuotations}
      />

      {/* Sale Modal — Direct Sale Onboarding Form */}
      <CustomerFormModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        mode="add"
        isSalesForm={true}
        onSuccess={() => {
          setIsContactModalOpen(false);
          fetchQuotations();
        }}
      />

      {/* Received Amount Modal for Direct Sales */}
      <CreditPaymentForm
        isOpen={isCreditPaymentFormOpen}
        onClose={() => setIsCreditPaymentFormOpen(false)}
        projects={selectedProjectForPayment ? [selectedProjectForPayment] : []}
        defaultStage="ordering"
        readOnlyStage={true}
        onConfirm={async (data) => {
          try {
            const payload = {
              amount: Number(data.amount),
              paymentStage: data.stage,
              paymentDate: data.paymentDate,
              paymentMethod: data.method,
              transactionId: data.transactionId,
              bankName: data.bankName,
              branch: data.branch,
              ifscCode: data.ifscCode,
              upiPhone: data.upiPhone,
              receiverName: data.receiverName,
              receiverPhone: data.receiverPhone,
              chequeNumber: data.chequeNumber,
              chequeDate: data.chequeDate,
              remarks: data.remarks,
              relatedProjectId: data.relatedProjectId,
              relatedProjectCollection: data.relatedProjectCollection
            };

            await API.post("/received-amounts", payload);
            toast.success("Received amount recorded successfully");
            setIsCreditPaymentFormOpen(false);
          } catch (err) {
            console.error("Submit Error:", err);
            toast.error(err.response?.data?.message || "Failed to record amount");
          }
        }}
      />
    </div>
  );
};
